import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { PiPencil, PiTrash } from 'react-icons/pi';
import { useLocation, useParams } from 'wouter';
import { JURISDICTION_LIST } from '@/lib/constants';
import {
  TBankAccount,
  TChangeStamp,
  TCryptoWallet,
  TInvoicingRule,
  TVatRate,
  useActivateCompany,
  useCloneVatRatesFromActive,
  useCompanyReadiness,
  useCreateBankAccount,
  useCreateCryptoWallet,
  useDeactivateCompany,
  useDeleteBankAccount,
  useDeleteCompany,
  useDeleteCryptoWallet,
  useDeleteVatRate,
  useInvoicingCompanies,
  useInvoicingCompany,
  useUpdateBankAccount,
  useUpdateCompany,
  useUpdateCryptoWallet,
  useUpsertVatRate,
} from '@/models/invoicingCompanies';
import ChangeStamp from './ChangeStamp';
import LastRecordedChange from './LastRecordedChange';
import { MONDAY_AR_LABELS, MONDAY_FINANCE_LABELS } from './mondayLabels';
import { listUrl } from '@/lib/listMemory';

const VAT_CELLS: Array<{
  label: string;
  buyerType: 'Individual' | 'Corporate';
  buyerLocation: 'domestic' | 'eu' | 'non_eu';
  buyerVatRegistered: boolean | null;
}> = [
  {
    label: 'Domestic Individual',
    buyerType: 'Individual',
    buyerLocation: 'domestic',
    buyerVatRegistered: null,
  },
  {
    label: 'Domestic Corp. VAT-obliged',
    buyerType: 'Corporate',
    buyerLocation: 'domestic',
    buyerVatRegistered: true,
  },
  {
    label: 'Domestic Corp. non-obliged',
    buyerType: 'Corporate',
    buyerLocation: 'domestic',
    buyerVatRegistered: false,
  },
  {
    label: 'Export Individual (EU)',
    buyerType: 'Individual',
    buyerLocation: 'eu',
    buyerVatRegistered: null,
  },
  {
    label: 'Export Individual (non-EU)',
    buyerType: 'Individual',
    buyerLocation: 'non_eu',
    buyerVatRegistered: null,
  },
  {
    label: 'Export Corp. EU VAT-obliged',
    buyerType: 'Corporate',
    buyerLocation: 'eu',
    buyerVatRegistered: true,
  },
  {
    label: 'Export Corp. EU non-obliged',
    buyerType: 'Corporate',
    buyerLocation: 'eu',
    buyerVatRegistered: false,
  },
  {
    label: 'Export Corp. non-EU VAT-obliged',
    buyerType: 'Corporate',
    buyerLocation: 'non_eu',
    buyerVatRegistered: true,
  },
  {
    label: 'Export Corp. non-EU non-obliged',
    buyerType: 'Corporate',
    buyerLocation: 'non_eu',
    buyerVatRegistered: false,
  },
];

export default function InvoicingCompanyDetail() {
  const params = useParams();
  const id = Number(params.id);
  const [, nav] = useLocation();
  const { data, isLoading } = useInvoicingCompany(id);

  if (isLoading || !data) return <Loader />;

  const jurisdictionTitle =
    JURISDICTION_LIST.find((j) => j.code === data.countryCode)?.title ??
    data.countryCode;
  const leviesVat = data.vatRegime !== 'none';

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <Title order={3}>{data.name}</Title>
          <Badge color={data.isActive ? 'green' : 'gray'}>
            {data.isActive ? 'Active' : 'Inactive'}
          </Badge>
          {!leviesVat && (
            <Badge color="grape" variant="light">
              No VAT
            </Badge>
          )}
          <Text c="dimmed">{jurisdictionTitle}</Text>
        </Group>
        <Button
          variant="subtle"
          onClick={() => nav(`~${listUrl('/invoicing-companies')}`)}
        >
          Back
        </Button>
      </Group>

      <LastRecordedChange change={data.lastRecordedChange} />

      <Tabs defaultValue="header">
        <Tabs.List>
          <Tabs.Tab value="header">Header</Tabs.Tab>
          <Tabs.Tab value="rules">Selection rules</Tabs.Tab>
          {/* An entity that levies no VAT has no rates to maintain, so the tab
              would only offer configuration nothing reads. */}
          {leviesVat && (
            <Tabs.Tab value="vat">VAT rates ({data.vatRates.length})</Tabs.Tab>
          )}
          <Tabs.Tab value="banks">
            Bank accounts ({data.bankAccounts.length})
          </Tabs.Tab>
          <Tabs.Tab value="wallets">
            Crypto wallets ({data.cryptoWallets.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="header" pt="md">
          <HeaderTab data={data} />
        </Tabs.Panel>
        <Tabs.Panel value="rules" pt="md">
          <SelectionRulesTab data={data} />
        </Tabs.Panel>
        {leviesVat && (
          <Tabs.Panel value="vat" pt="md">
            <VatRatesTab data={data} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="banks" pt="md">
          <BankAccountsTab data={data} />
        </Tabs.Panel>
        <Tabs.Panel value="wallets" pt="md">
          <CryptoWalletsTab data={data} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/** Places a field's own change date directly beneath it. */
function Stamped({
  stamp,
  children,
}: {
  stamp: TChangeStamp | undefined;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={2}>
      {children}
      <ChangeStamp stamp={stamp} />
    </Stack>
  );
}

function HeaderTab({
  data,
}: {
  data: ReturnType<typeof useInvoicingCompany>['data'] & {};
}) {
  const [, nav] = useLocation();
  const updateMut = useUpdateCompany();
  const activateMut = useActivateCompany();
  const deactivateMut = useDeactivateCompany();
  const deleteMut = useDeleteCompany();
  const { data: readiness } = useCompanyReadiness(data.id);

  const [form, setForm] = useState({
    name: data.name,
    registrationNumber: data.registrationNumber ?? '',
    vatNumber: data.vatNumber ?? '',
    legalAddress: data.legalAddress,
    vatRate: Number(data.vatRate) || 0,
    vatLineDisplay: data.vatLineDisplay ?? 'dash',
    priority: data.priority,
    website: data.website ?? '',
    mondayFinanceLabel: data.mondayFinanceLabel ?? '',
    mondayArLabel: data.mondayArLabel ?? '',
  });

  const stamps = data.fieldChanges ?? {};
  const canDelete = data.invoiceCount === 0;

  return (
    <Stack gap="md" maw={880}>
      <Alert color="blue" variant="light">
        Edits here affect future invoices only. Invoices already issued preserve
        the values captured at their issue date.
      </Alert>
      <Stamped stamp={stamps.name}>
        <TextInput
          label="Name"
          value={form.name}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setForm((f) => ({ ...f, name: v }));
          }}
        />
      </Stamped>
      <Group grow>
        <Stamped stamp={stamps.registrationNumber}>
          <TextInput
            label="Registration number"
            value={form.registrationNumber}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setForm((f) => ({ ...f, registrationNumber: v }));
            }}
          />
        </Stamped>
        <Stamped stamp={stamps.vatNumber}>
          <TextInput
            label="VAT number"
            value={form.vatNumber}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setForm((f) => ({ ...f, vatNumber: v }));
            }}
          />
        </Stamped>
      </Group>
      <Stamped stamp={stamps.legalAddress}>
        <Textarea
          label="Legal address"
          autosize
          minRows={2}
          value={form.legalAddress}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setForm((f) => ({ ...f, legalAddress: v }));
          }}
        />
      </Stamped>
      <Group grow>
        {/* A fallback rate is meaningless where nothing is levied; what such
            an entity needs to state instead is how its invoices show it. */}
        {data.vatRegime === 'none' ? (
          <Stamped stamp={stamps.vatLineDisplay}>
            <Select
              label="Tax line on invoices"
              description="This entity levies no VAT"
              data={[
                { value: 'dash', label: 'Show "TAX -"' },
                { value: 'omit', label: 'No tax line at all' },
              ]}
              allowDeselect={false}
              value={form.vatLineDisplay}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  vatLineDisplay: (v as 'omit' | 'dash') ?? 'dash',
                }))
              }
            />
          </Stamped>
        ) : (
          <Stamped stamp={stamps.vatRate}>
            <NumberInput
              label="Default VAT rate (fallback)"
              min={0}
              max={1}
              step={0.01}
              decimalScale={4}
              value={form.vatRate}
              onChange={(v) =>
                setForm((f) => ({ ...f, vatRate: Number(v) || 0 }))
              }
            />
          </Stamped>
        )}
        <Stamped stamp={stamps.priority}>
          <NumberInput
            label="Priority"
            min={1}
            value={form.priority}
            onChange={(v) =>
              setForm((f) => ({ ...f, priority: Number(v) || 100 }))
            }
          />
        </Stamped>
      </Group>
      <Stamped stamp={stamps.website}>
        <TextInput
          label="Website"
          value={form.website}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setForm((f) => ({ ...f, website: v }));
          }}
        />
      </Stamped>

      <Divider label="Monday.com" labelPosition="left" mt="xs" />
      <Group grow align="flex-start">
        <Stamped stamp={stamps.mondayFinanceLabel}>
          <Select
            label="Finance board company"
            description='Column "Our Company Issuing Contract/Invoice"'
            placeholder="Leave the cell empty"
            data={[...MONDAY_FINANCE_LABELS]}
            clearable
            value={form.mondayFinanceLabel || null}
            onChange={(v) =>
              setForm((f) => ({ ...f, mondayFinanceLabel: v ?? '' }))
            }
          />
        </Stamped>
        <Stamped stamp={stamps.mondayArLabel}>
          <Select
            label="Accounting Requests company"
            description='Subitem column "Status company name"'
            placeholder="Leave the cell empty"
            data={[...MONDAY_AR_LABELS]}
            clearable
            value={form.mondayArLabel || null}
            onChange={(v) => setForm((f) => ({ ...f, mondayArLabel: v ?? '' }))}
          />
        </Stamped>
      </Group>

      {readiness && !readiness.ready && !data.isActive && (
        <Alert color="orange" variant="light">
          <Text fw={500}>Not ready to activate:</Text>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
            {readiness.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Group justify="space-between" mt="md">
        <Group>
          <Tooltip
            label={readiness?.issues.join('; ') ?? ''}
            disabled={data.isActive || (readiness?.ready ?? false)}
          >
            <Button
              color="green"
              disabled={data.isActive || !(readiness?.ready ?? false)}
              loading={activateMut.isPending}
              onClick={() => activateMut.mutate(data.id)}
            >
              Activate
            </Button>
          </Tooltip>
          <Button
            variant="outline"
            disabled={!data.isActive}
            loading={deactivateMut.isPending}
            onClick={() => deactivateMut.mutate(data.id)}
          >
            Deactivate
          </Button>
        </Group>
        <Group>
          <Tooltip
            label="Company has issued invoices; deactivate instead"
            disabled={canDelete}
          >
            <Button
              color="red"
              variant="outline"
              disabled={!canDelete}
              loading={deleteMut.isPending}
              onClick={() => {
                if (!confirm(`Delete company "${data.name}"?`)) return;
                deleteMut.mutate(data.id, {
                  onSuccess: () => nav(`~${listUrl('/invoicing-companies')}`),
                });
              }}
            >
              Delete
            </Button>
          </Tooltip>
          <Button
            loading={updateMut.isPending}
            onClick={() => {
              const { vatLineDisplay, ...rest } = form;
              // Only an entity that levies nothing has a line display to
              // state; sending one for a VAT-levying company is rejected.
              updateMut.mutate({
                id: data.id,
                ...rest,
                ...(data.vatRegime === 'none' ? { vatLineDisplay } : {}),
              });
            }}
          >
            Save
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

function VatRatesTab({
  data,
}: {
  data: ReturnType<typeof useInvoicingCompany>['data'] & {};
}) {
  const upsertMut = useUpsertVatRate(data.id);
  const deleteMut = useDeleteVatRate(data.id);
  const cloneMut = useCloneVatRatesFromActive(data.id);
  const [editing, setEditing] = useState<null | {
    cell: (typeof VAT_CELLS)[number];
    existing?: TVatRate;
  }>(null);

  const rateByCell = useMemo(() => {
    const m = new Map<string, TVatRate>();
    for (const r of data.vatRates) {
      const key = `${r.buyerType}|${r.buyerLocation}|${r.buyerVatRegistered ?? 'null'}`;
      m.set(key, r);
    }
    return m;
  }, [data.vatRates]);

  const keyOf = (c: (typeof VAT_CELLS)[number]) =>
    `${c.buyerType}|${c.buyerLocation}|${c.buyerVatRegistered ?? 'null'}`;

  return (
    <Stack gap="md" maw={880}>
      {!data.isActive && data.vatRates.length < 9 && (
        <Group>
          <Button
            variant="light"
            loading={cloneMut.isPending}
            onClick={() => cloneMut.mutate()}
          >
            Clone from current active in this jurisdiction
          </Button>
        </Group>
      )}
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Combination</Table.Th>
              <Table.Th>Rate</Table.Th>
              <Table.Th>Invoice note</Table.Th>
              <Table.Th>Last changed</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {VAT_CELLS.map((cell) => {
              const existing = rateByCell.get(keyOf(cell));
              return (
                <Table.Tr key={cell.label}>
                  <Table.Td>{cell.label}</Table.Td>
                  <Table.Td>
                    {existing ? (
                      `${(Number(existing.vatRate) * 100).toFixed(2)}%`
                    ) : (
                      <Text c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {existing?.invoiceNote ?? ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <ChangeStamp stamp={existing?.lastChange} variant="row" />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => setEditing({ cell, existing })}
                      >
                        <PiPencil />
                      </ActionIcon>
                      {existing && (
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => deleteMut.mutate(existing.id)}
                        >
                          <PiTrash />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {editing && (
        <VatRateModal
          cell={editing.cell}
          existing={editing.existing}
          onClose={() => setEditing(null)}
          onSave={(vatRate, invoiceNote) => {
            upsertMut.mutate(
              {
                buyerType: editing.cell.buyerType,
                buyerLocation: editing.cell.buyerLocation,
                buyerVatRegistered: editing.cell.buyerVatRegistered,
                vatRate,
                invoiceNote,
              },
              { onSuccess: () => setEditing(null) },
            );
          }}
          saving={upsertMut.isPending}
        />
      )}
    </Stack>
  );
}

function VatRateModal({
  cell,
  existing,
  onClose,
  onSave,
  saving,
}: {
  cell: (typeof VAT_CELLS)[number];
  existing?: TVatRate;
  onClose: () => void;
  onSave: (rate: number, note: string | null) => void;
  saving: boolean;
}) {
  const [rate, setRate] = useState<number | string>(
    existing ? Number(existing.vatRate) : 0,
  );
  const [note, setNote] = useState(existing?.invoiceNote ?? '');

  return (
    <Modal opened onClose={onClose} title={cell.label} size="md">
      <Stack gap="md">
        <NumberInput
          label="VAT rate (0 to 1)"
          min={0}
          max={1}
          step={0.01}
          decimalScale={4}
          value={rate}
          onChange={setRate}
        />
        <Textarea
          label="Invoice note"
          autosize
          minRows={3}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={saving}
            onClick={() => onSave(Number(rate) || 0, note.trim() || null)}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function BankAccountsTab({
  data,
}: {
  data: ReturnType<typeof useInvoicingCompany>['data'] & {};
}) {
  const createMut = useCreateBankAccount(data.id);
  const updateMut = useUpdateBankAccount(data.id);
  const deleteMut = useDeleteBankAccount(data.id);
  const [editing, setEditing] = useState<null | TBankAccount | 'new'>(null);

  return (
    <Stack gap="md" maw={880}>
      <Alert color="blue" variant="light">
        Bank account changes apply to future invoices only. Already-issued
        invoices keep the payment details captured at their issue date.
      </Alert>
      <Group justify="flex-end">
        <Button onClick={() => setEditing('new')}>Add bank account</Button>
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Currency</Table.Th>
              <Table.Th>Beneficiary</Table.Th>
              <Table.Th>Account</Table.Th>
              <Table.Th>Bank</Table.Th>
              <Table.Th>SWIFT</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Last changed</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.bankAccounts.map((b) => (
              <Table.Tr key={b.id}>
                <Table.Td>{b.currency}</Table.Td>
                <Table.Td>{b.beneficiaryName}</Table.Td>
                <Table.Td>{b.accountNumber}</Table.Td>
                <Table.Td>{b.bankName}</Table.Td>
                <Table.Td>{b.swiftCode}</Table.Td>
                <Table.Td>
                  <Badge color={b.isActive ? 'green' : 'gray'}>
                    {b.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <ChangeStamp stamp={b.lastChange} variant="row" />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon variant="subtle" onClick={() => setEditing(b)}>
                      <PiPencil />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        if (!confirm('Delete this bank account?')) return;
                        deleteMut.mutate(b.id);
                      }}
                    >
                      <PiTrash />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {editing && (
        <BankAccountModal
          initial={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSave={(input) => {
            if (editing === 'new')
              createMut.mutate(input, { onSuccess: () => setEditing(null) });
            else
              updateMut.mutate(
                { id: editing.id, ...input },
                { onSuccess: () => setEditing(null) },
              );
          }}
          saving={createMut.isPending || updateMut.isPending}
        />
      )}
    </Stack>
  );
}

function BankAccountModal({
  initial,
  onClose,
  onSave,
  saving,
}: {
  initial?: TBankAccount;
  onClose: () => void;
  onSave: (
    input: Omit<TBankAccount, 'id' | 'companyId' | 'lastChange'>,
  ) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    currency: initial?.currency ?? 'EUR',
    beneficiaryName: initial?.beneficiaryName ?? '',
    beneficiaryAddress: initial?.beneficiaryAddress ?? '',
    accountNumber: initial?.accountNumber ?? '',
    bankName: initial?.bankName ?? '',
    bankAddress: initial?.bankAddress ?? '',
    swiftCode: initial?.swiftCode ?? '',
    correspondentBankName: initial?.correspondentBankName ?? '',
    correspondentBankCountry: initial?.correspondentBankCountry ?? '',
    correspondentBankSwift: initial?.correspondentBankSwift ?? '',
    isActive: initial?.isActive ?? true,
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal
      opened
      onClose={onClose}
      title={initial ? 'Edit bank account' : 'New bank account'}
      size="lg"
    >
      <Stack gap="sm">
        <Group grow>
          <Select
            label="Currency"
            data={['EUR', 'USD', 'GBP']}
            value={form.currency}
            onChange={(v) => set('currency', v ?? 'EUR')}
          />
          <Checkbox
            label="Active"
            checked={form.isActive}
            onChange={(e) => set('isActive', e.currentTarget.checked)}
            mt="lg"
          />
        </Group>
        <TextInput
          label="Beneficiary name"
          value={form.beneficiaryName}
          onChange={(e) => set('beneficiaryName', e.currentTarget.value)}
        />
        <Textarea
          label="Beneficiary address"
          autosize
          minRows={1}
          value={form.beneficiaryAddress}
          onChange={(e) => set('beneficiaryAddress', e.currentTarget.value)}
        />
        <Group grow>
          <TextInput
            label="Account number"
            value={form.accountNumber}
            onChange={(e) => set('accountNumber', e.currentTarget.value)}
          />
          <TextInput
            label="SWIFT"
            value={form.swiftCode}
            onChange={(e) => set('swiftCode', e.currentTarget.value)}
          />
        </Group>
        <TextInput
          label="Bank name"
          value={form.bankName}
          onChange={(e) => set('bankName', e.currentTarget.value)}
        />
        <Textarea
          label="Bank address"
          autosize
          minRows={1}
          value={form.bankAddress}
          onChange={(e) => set('bankAddress', e.currentTarget.value)}
        />
        <Title order={6} mt="sm">
          Correspondent bank (optional)
        </Title>
        <Group grow>
          <TextInput
            label="Name"
            value={form.correspondentBankName}
            onChange={(e) =>
              set('correspondentBankName', e.currentTarget.value)
            }
          />
          <TextInput
            label="Country"
            value={form.correspondentBankCountry}
            onChange={(e) =>
              set('correspondentBankCountry', e.currentTarget.value)
            }
          />
          <TextInput
            label="SWIFT"
            value={form.correspondentBankSwift}
            onChange={(e) =>
              set('correspondentBankSwift', e.currentTarget.value)
            }
          />
        </Group>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => onSave(form)}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CryptoWalletsTab({
  data,
}: {
  data: ReturnType<typeof useInvoicingCompany>['data'] & {};
}) {
  const createMut = useCreateCryptoWallet(data.id);
  const updateMut = useUpdateCryptoWallet(data.id);
  const deleteMut = useDeleteCryptoWallet(data.id);
  const [editing, setEditing] = useState<null | TCryptoWallet | 'new'>(null);

  return (
    <Stack gap="md" maw={880}>
      <Alert color="blue" variant="light">
        Wallet changes apply to future invoices only. Already-issued invoices
        keep the wallet addresses captured at their issue date.
      </Alert>
      <Group justify="flex-end">
        <Button onClick={() => setEditing('new')}>Add crypto wallet</Button>
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Chain</Table.Th>
              <Table.Th>Network</Table.Th>
              <Table.Th>Address</Table.Th>
              <Table.Th>Tokens</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Last changed</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.cryptoWallets.map((w) => (
              <Table.Tr key={w.id}>
                <Table.Td>{w.chain}</Table.Td>
                <Table.Td>{w.network}</Table.Td>
                <Table.Td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {w.walletAddress}
                </Table.Td>
                <Table.Td>{w.acceptedTokens.join(', ')}</Table.Td>
                <Table.Td>
                  <Badge color={w.isActive ? 'green' : 'gray'}>
                    {w.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <ChangeStamp stamp={w.lastChange} variant="row" />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon variant="subtle" onClick={() => setEditing(w)}>
                      <PiPencil />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        if (!confirm('Delete this wallet?')) return;
                        deleteMut.mutate(w.id);
                      }}
                    >
                      <PiTrash />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {editing && (
        <CryptoWalletModal
          initial={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSave={(input) => {
            if (editing === 'new')
              createMut.mutate(input, { onSuccess: () => setEditing(null) });
            else
              updateMut.mutate(
                { id: editing.id, ...input },
                { onSuccess: () => setEditing(null) },
              );
          }}
          saving={createMut.isPending || updateMut.isPending}
        />
      )}
    </Stack>
  );
}

function CryptoWalletModal({
  initial,
  onClose,
  onSave,
  saving,
}: {
  initial?: TCryptoWallet;
  onClose: () => void;
  onSave: (
    input: Omit<TCryptoWallet, 'id' | 'companyId' | 'lastChange'>,
  ) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    chain: initial?.chain ?? '',
    network: initial?.network ?? '',
    walletAddress: initial?.walletAddress ?? '',
    acceptedTokens: initial?.acceptedTokens ?? [],
    isActive: initial?.isActive ?? true,
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal
      opened
      onClose={onClose}
      title={initial ? 'Edit wallet' : 'New wallet'}
      size="lg"
    >
      <Stack gap="sm">
        <Group grow>
          <TextInput
            label="Chain"
            value={form.chain}
            onChange={(e) => set('chain', e.currentTarget.value)}
          />
          <TextInput
            label="Network"
            value={form.network}
            onChange={(e) => set('network', e.currentTarget.value)}
          />
        </Group>
        <TextInput
          label="Wallet address"
          value={form.walletAddress}
          onChange={(e) => set('walletAddress', e.currentTarget.value)}
        />
        <MultiSelect
          label="Accepted tokens"
          data={['USDT', 'USDC', 'DAI', 'ETH', 'BTC']}
          searchable
          value={form.acceptedTokens}
          onChange={(v) => set('acceptedTokens', v)}
        />
        <Checkbox
          label="Active"
          checked={form.isActive}
          onChange={(e) => set('isActive', e.currentTarget.checked)}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => onSave(form)}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Countries whose entity may invoice a payer based in the same country. */
const DOMESTIC_SALE_ALLOWED = new Set(['EE', 'PL']);

/** Mirrors EU_COUNTRY_CODES in the backend's invoicing constants. */
const EU_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
]);

const REQUIRED_VAT_ROWS = 9;

/** Currencies an invoice can be issued in. */
const INVOICE_CURRENCIES = ['EUR', 'USD'];

/**
 * Service type treated as registering a company. Interim proxy until services
 * carry an explicit marker, so the page must disclose it rather than promise a
 * precision the backend does not yet have.
 */
const REGISTRATION_SERVICE_TYPE = 'Corporate Services';

const countryName = (code: string) =>
  JURISDICTION_LIST.find((j) => j.code === code)?.title ?? code;

const ruleSentence = (rule: TInvoicingRule): string => {
  switch (rule.ruleType) {
    case 'jurisdiction':
      return `Sale covers ${countryName(rule.ruleKey)}`;
    case 'industry':
      return `Client industry is ${rule.ruleKey}`;
    case 'paying_party_country':
      return `Payer is based in ${countryName(rule.ruleKey)}`;
    case 'payment_method':
      return `Client pays by ${rule.ruleKey}`;
    case 'service_category':
      return `Sale includes ${rule.ruleKey}`;
    default:
      return `${rule.ruleType}: ${rule.ruleKey}`;
  }
};

function Yes({ note }: { note?: string }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Badge size="sm" color="green" variant="light">
        Yes
      </Badge>
      {note && (
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      )}
    </Group>
  );
}

function No({ note }: { note?: string }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Badge size="sm" color="red" variant="light">
        No
      </Badge>
      {note && (
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      )}
    </Group>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={8}>
      <Text fw={600} size="sm">
        {title}
      </Text>
      {children}
    </Stack>
  );
}

/**
 * Read-only eligibility matrices: exactly when this company may and may not
 * issue an invoice. Stated as tables of concrete cases rather than rules, so an
 * accountant can check a real situation against it without knowing the engine.
 */
function SelectionRulesTab({
  data,
}: {
  data: ReturnType<typeof useInvoicingCompany>['data'] & {};
}) {
  const {
    data: allCompanies,
    isSuccess: companiesLoaded,
    isError: companiesFailed,
  } = useInvoicingCompanies();

  const country = countryName(data.countryCode);
  const domesticAllowed = DOMESTIC_SALE_ALLOWED.has(data.countryCode);
  // buyer_location 'eu' excludes the seller's own country, so an EU entity must
  // not label those rows in a way that swallows its domestic cases.
  const isEuEntity = EU_COUNTRY_CODES.has(data.countryCode);
  const euLabel = isEuEntity ? 'another EU country' : 'the EU';
  const nonEuLabel = isEuEntity
    ? 'outside the EU'
    : `outside the EU, other than ${country}`;

  const activeCurrencies = new Set(
    data.bankAccounts.filter((a) => a.isActive).map((a) => a.currency),
  );
  const activeWallets = data.cryptoWallets.filter((w) => w.isActive).length;
  const vatRowsCorrect = data.vatRates.length === REQUIRED_VAT_ROWS;
  const exclusions = data.rules.filter((r) => !r.isAllowed);
  const methodBlocked = (method: string) =>
    exclusions.some(
      (r) => r.ruleType === 'payment_method' && r.ruleKey === method,
    );
  // Exclusions that cannot be resolved against a table row on their own.
  const conditional = exclusions.some((r) => r.ruleType !== 'payment_method')
    ? 'unless an exclusion below applies'
    : undefined;

  const buyerCases: Array<{
    label: string;
    buyerType: 'Individual' | 'Corporate';
    buyerLocation: 'domestic' | 'eu' | 'non_eu';
    buyerVatRegistered: boolean | null;
  }> = [
    {
      label: `Individual in ${country}`,
      buyerType: 'Individual',
      buyerLocation: 'domestic',
      buyerVatRegistered: null,
    },
    {
      label: `Company in ${country}, VAT-registered`,
      buyerType: 'Corporate',
      buyerLocation: 'domestic',
      buyerVatRegistered: true,
    },
    {
      label: `Company in ${country}, not VAT-registered`,
      buyerType: 'Corporate',
      buyerLocation: 'domestic',
      buyerVatRegistered: false,
    },
    {
      label: `Individual in ${euLabel}`,
      buyerType: 'Individual',
      buyerLocation: 'eu',
      buyerVatRegistered: null,
    },
    {
      label: `Individual ${nonEuLabel}`,
      buyerType: 'Individual',
      buyerLocation: 'non_eu',
      buyerVatRegistered: null,
    },
    {
      label: `Company in ${euLabel}, VAT-registered`,
      buyerType: 'Corporate',
      buyerLocation: 'eu',
      buyerVatRegistered: true,
    },
    {
      label: `Company in ${euLabel}, not VAT-registered`,
      buyerType: 'Corporate',
      buyerLocation: 'eu',
      buyerVatRegistered: false,
    },
    {
      label: `Company ${nonEuLabel}, VAT-registered`,
      buyerType: 'Corporate',
      buyerLocation: 'non_eu',
      buyerVatRegistered: true,
    },
    {
      label: `Company ${nonEuLabel}, not VAT-registered`,
      buyerType: 'Corporate',
      buyerLocation: 'non_eu',
      buyerVatRegistered: false,
    },
  ];

  const rateFor = (c: (typeof buyerCases)[number]) =>
    data.vatRates.find(
      (r) =>
        r.buyerType === c.buyerType &&
        r.buyerLocation === c.buyerLocation &&
        r.buyerVatRegistered === c.buyerVatRegistered,
    );

  const ranked = [...(allCompanies ?? [])].sort(
    (a, b) => a.priority - b.priority || a.name.localeCompare(b.name),
  );
  const sameRank = ranked.filter(
    (c) => c.isActive && c.id !== data.id && c.priority === data.priority,
  );

  return (
    <Stack gap="lg" maw={880}>
      <Group gap="xs">
        <Badge color={data.isActive ? 'green' : 'gray'} variant="light">
          {data.isActive ? 'Active' : 'Inactive'}
        </Badge>
        <Badge color={vatRowsCorrect ? 'green' : 'red'} variant="light">
          VAT rows {data.vatRates.length} of {REQUIRED_VAT_ROWS}
        </Badge>
        <Text size="xs" c="dimmed">
          Both must hold before any case below can apply.
        </Text>
      </Group>

      {!data.isActive && (
        <Alert color="gray" title="Not in service">
          This company is inactive, so none of the cases below apply. It is
          never chosen until it is activated.
        </Alert>
      )}

      <Section title={`Which buyers ${data.name} may invoice`}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Buyer</Table.Th>
              <Table.Th w={230}>May invoice</Table.Th>
              <Table.Th w={110}>VAT</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {buyerCases.map((c) => {
              const rate = rateFor(c);
              const eligible =
                c.buyerLocation !== 'domestic' || domesticAllowed;
              return (
                <Table.Tr key={c.label}>
                  <Table.Td>
                    <Text size="sm">{c.label}</Text>
                  </Table.Td>
                  <Table.Td>
                    {eligible ? (
                      <Yes note={conditional} />
                    ) : (
                      <No note={`payer is in ${country}`} />
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!eligible ? (
                      <Text size="sm" c="dimmed">
                        never applies
                      </Text>
                    ) : rate ? (
                      <Text size="sm" fw={500}>
                        {(Number(rate.vatRate) * 100).toFixed(0)}%
                      </Text>
                    ) : (
                      <Text size="sm" c="red">
                        not set
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        {!vatRowsCorrect && (
          <Alert color="red" variant="light">
            This company has {data.vatRates.length} VAT rows. Selection counts
            the rows and requires exactly {REQUIRED_VAT_ROWS}, so until the VAT
            rates tab holds that many this company is never chosen for any sale.
            The count alone does not prove every buyer above is covered — check
            the rows themselves.
          </Alert>
        )}
      </Section>

      <Section title="How the client may pay">
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Payment</Table.Th>
              <Table.Th w={330}>May be used</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {INVOICE_CURRENCIES.map((cur) => (
              <Table.Tr key={cur}>
                <Table.Td>
                  <Text size="sm">Bank transfer in {cur}</Text>
                </Table.Td>
                <Table.Td>
                  {methodBlocked('Bank Transfer') ? (
                    <No note="bank transfer is excluded by configuration" />
                  ) : activeCurrencies.has(cur) ? (
                    <Yes note={`active ${cur} account`} />
                  ) : (
                    <No note={`no active ${cur} account`} />
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td>
                <Text size="sm">Cryptocurrency</Text>
              </Table.Td>
              <Table.Td>
                {methodBlocked('Cryptocurrency') ? (
                  <No note="crypto is excluded by configuration" />
                ) : activeWallets > 0 ? (
                  <Yes
                    note={`${activeWallets} active ${
                      activeWallets === 1 ? 'wallet' : 'wallets'
                    }`}
                  />
                ) : (
                  <No note="no active wallet" />
                )}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Section>

      <Section title="What the sale may contain">
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Sale</Table.Th>
              <Table.Th w={330}>May invoice</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>
                <Text size="sm">Registering a company in {country}</Text>
              </Table.Td>
              <Table.Td>
                <No note={`cannot sell a ${country} company`} />
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text size="sm">Other services in {country}</Text>
              </Table.Td>
              <Table.Td>
                <Yes note={conditional} />
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text size="sm">Anything outside {country}</Text>
              </Table.Td>
              <Table.Td>
                <Yes note={conditional} />
              </Table.Td>
            </Table.Tr>
            {exclusions.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <Text size="sm">{ruleSentence(r)}</Text>
                </Table.Td>
                <Table.Td>
                  <No note="configured exclusion" />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Text size="xs" c="dimmed">
          Everything catalogued as "{REGISTRATION_SERVICE_TYPE}" counts as
          registering a company until services are marked individually, so some
          corporate services that are not registrations are refused in {country}{' '}
          too.
          {exclusions.length === 0 &&
            ' No industry, service-category, payer-country or jurisdiction exclusions are configured for this company.'}
        </Text>
      </Section>

      <Section title="If more than one company qualifies">
        <Text size="sm">
          The lowest priority number wins. Priority never overrides the tables
          above.
        </Text>
        {companiesFailed && (
          <Text size="sm" c="red">
            Could not load the other companies, so the order cannot be shown.
          </Text>
        )}
        {!companiesLoaded && !companiesFailed && (
          <Text size="sm" c="dimmed">
            Loading the order...
          </Text>
        )}
        {companiesLoaded && (
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={90}>Priority</Table.Th>
                <Table.Th>Company</Table.Th>
                <Table.Th w={160}>Jurisdiction</Table.Th>
                <Table.Th w={110}>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ranked.map((c) => {
                const isSelf = c.id === data.id;
                return (
                  <Table.Tr
                    key={c.id}
                    style={
                      isSelf
                        ? { backgroundColor: 'var(--mantine-color-blue-light)' }
                        : undefined
                    }
                  >
                    <Table.Td>
                      <Text size="sm" fw={isSelf ? 700 : 400}>
                        {c.priority}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={isSelf ? 700 : 400}>
                          {c.name}
                        </Text>
                        {isSelf && (
                          <Badge size="xs" color="blue">
                            this company
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{countryName(c.countryCode)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="xs"
                        variant="light"
                        color={c.isActive ? 'green' : 'gray'}
                      >
                        {c.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
        {companiesLoaded && sameRank.length > 0 && (
          <Text size="sm" c="orange">
            {sameRank.map((c) => c.name).join(', ')}{' '}
            {sameRank.length === 1 ? 'shares' : 'share'} priority{' '}
            {data.priority} with this company, so priority alone does not decide
            between them.
          </Text>
        )}
      </Section>
    </Stack>
  );
}
